<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';
const searchPlaceholderProp = __MACROS_toRef(__props, "searchPlaceholder");
const placeholderProp = __MACROS_toRef(__props, "placeholder");
const labelForValueProp = __MACROS_toRef(__props, "labelForValue");
const value = __MACROS_toRef(__props, "modelValue");

import { toRef as __MACROS_toRef } from "vue";
/**
 * GSheetSelectInput - A responsive select input component
 *
 * Desktop: Uses Headless UI Combobox with floating dropdown
 * Mobile: Uses action sheet (bottom sheet) with pending selection
 *
 * API is compatible with GSelectInput for easy migration.
 */
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import { Float } from '@headlessui-float/vue'
import { removeBy } from '@shared/array'
import { removeDecimals } from '@consumer/helpers/number'
import { identity } from '@shared/functions'
import { isPresent, isEqual, isNil, isObject } from '@consumer/helpers/object'
import { filterItems, buildRegExp, sortedTokensFor } from '@consumer/services/filter'
import { typeaheadDebounce } from '@shared/debounce'
import type { PathHelper } from '@corp/services/api'
import { faChevronDown } from '@fortawesome/pro-light-svg-icons'
import { faCheck } from '@fortawesome/pro-regular-svg-icons'
import { isMobile } from '@consumer/utils/breakpoints'
import { preventWindowScroll } from '@shared/composables'

// =============================================================================
// Props & Emits
// =============================================================================

withDefaults(defineProps<{
  modelValue?: any
  options?: any[]
  multi?: boolean
  size?: 'small' | 'large'
  disabled?: boolean
  required?: boolean | string
  hasErrors?: boolean
  labelProp?: string
  valueProp?: string
  searchableProp?: string
  descriptionProp?: string
  selectedProp?: string
  wholeItem?: boolean
  allowCreate?: boolean
  labelForValue?: ((value: any) => string)
  unselectLabel?: string
  noUnselect?: boolean
  numeric?: boolean
  placeholder?: string
  type?: string
  fetchOptions?: PathHelper
  includeSelectedInOptions?: boolean
  collapseOnSelect?: boolean
  emptyQueryOptions?: any[]
  disableFiltering?: boolean
  title?: string
  cancelLabel?: string
  confirmLabel?: string
  /**
   * Auto-confirm selection without requiring Confirm button tap.
   * Only applies to single-select on mobile.
   */
  autoConfirm?: boolean
  searchable?: boolean
  pill?: boolean
  /**
   * Allow typing to filter/search options (without creating new items).
   * Use this when you want searchable dropdown behavior on desktop.
   */
  autocomplete?: boolean
  createLabel?: string
  suffix?: string
  inputMask?: string
  searchPlaceholder?: string
  /**
   * Optional name for identifying this component in tests.
   * Adds data-name attribute to input and options panel.
   */
  name?: string
}>(), { multi: false,disabled: false,required: false,hasErrors: false,size: 'large',labelProp: 'label',valueProp: 'value',descriptionProp: 'description',wholeItem: false,allowCreate: false,autocomplete: false,unselectLabel: 'Unselect',noUnselect: false,numeric: false,placeholder: 'Please Choose One',includeSelectedInOptions: false,emptyQueryOptions: () => ([]),cancelLabel: 'Cancel',confirmLabel: 'Confirm',autoConfirm: false,searchable: false,pill: false,createLabel: 'Add',suffix: '', })

const emit = defineEmits<{
  'update:modelValue': [value: any]
  focus: []
  blur: []
  cancel: []
  confirm: [value: any]
}>()

// =============================================================================
// State
// =============================================================================

const isActionSheet = computed(() => isMobile.value)
let filteringBy = _ref('')
let mobileOpen = _ref(false)

// Items created by the user in allowCreate mode
const createdItems = _ref<any[]>([])

// The committed selection (synced with modelValue)
let selectedItems = _ref<any[]>([])

// Mobile only: working selection before confirm
let pendingItems = _ref<any[]>([])

// Remote fetch state
let remoteItems = _ref<any[]>()
let fetchingRemoteItems = _ref(false)

// =============================================================================
// Refs
// =============================================================================

const mobileSearchInputRef = ref<HTMLInputElement>()
const comboboxButtonWrapperRef = ref<HTMLElement>()
const comboboxInputRef = ref<any>()

// =============================================================================
// Helpers
// =============================================================================

const labelForValue = __props.labelForValue || identity

const extractValue = __props.wholeItem
  ? (item: any) => item[__props.valueProp]
  : identity

function coerceValue (val: any) {
  return __props.numeric ? removeDecimals(val) : val
}

function findItemByValue (items: any[], val: any) {
  return items.find(item =>
    isEqual(String(item[__props.valueProp]).toLowerCase(), String(val).toLowerCase()),
  )
}

function createNewItem (val: any) {
  const newItem = __props.wholeItem && isObject(val)
    ? val
    : { [__props.labelProp]: labelForValue(val), [__props.valueProp]: coerceValue(val) }
  createdItems.value.push(newItem)
  return newItem
}

function removeCreatedItem (item: any) {
  const idx = createdItems.value.findIndex(i => i[__props.valueProp] === item[__props.valueProp])
  if (idx !== -1) {
    createdItems.value.splice(idx, 1)
  }
}

function isCreatedItem (item: any): boolean {
  return createdItems.value.some(c => c[__props.valueProp] === item[__props.valueProp])
}

/** Get label with suffix only for created items */
function labelWithSuffix (item: any): string {
  const label = item?.[__props.labelProp] || ''
  return isCreatedItem(item) ? `${label}${__props.suffix}` : label
}

// =============================================================================
// Computed: Selection
// =============================================================================

/** The current working selection for mobile */
const workingItems = _computed(() =>
  isActionSheet.value ? pendingItems.value : selectedItems.value,
)

/** Single selected item (for single-select mode) */
const selectedItem = _computed(() => !__props.multi && selectedItems.value[0])

// =============================================================================
// Computed: Options
// =============================================================================

/** All available items including user-created ones */
const allItems = _computed(() => {
  const base = __props.options
    || (__props.wholeItem && __props.modelValue && (__props.multi ? __props.modelValue : [__props.modelValue]))
    || []
  const withEmpty = base.concat(__props.emptyQueryOptions)
  return __props.allowCreate ? createdItems.value.concat(withEmpty) : withEmpty
})

/** Filtered items based on search query */
const filteredItems = _computed(() => {
  const notSelected = (item: any) =>
    __props.includeSelectedInOptions || !findItemByValue(workingItems.value, item[__props.valueProp])

  if (__props.fetchOptions) {
    return remoteItems.value?.filter(notSelected) || []
  }

  const items = __props.multi ? allItems.value.filter(notSelected) : allItems.value
  return filterItems(items, {
    query: filteringBy.value,
    searchableProp: __props.searchableProp ?? __props.labelProp,
  })
})

/** Whether to show the "create new" option (only when value doesn't exist in list) */
const showCreateOption = _computed(() =>
  __props.allowCreate
  && isPresent(filteringBy.value)
  && !allItems.value.find((item: any) =>
    item[__props.labelProp] === filteringBy.value || item[__props.valueProp] === filteringBy.value)
  && !remoteItems.value?.find((item: any) =>
    item[__props.labelProp] === filteringBy.value || item[__props.valueProp] === filteringBy.value),
)

/** Whether to show the "unselect" option */
const showUnselectOption = _computed(() => {
  const currentItem = !__props.multi && workingItems.value[0]
  return currentItem && !__props.noUnselect && !__props.required
})

/** Fake item for creating new */
const createOption = _computed(() => ({
  [__props.labelProp]: labelForValue(filteringBy.value),
  [__props.valueProp]: `__create__${filteringBy.value}`,
  creatable: true,
}))

/** Fake item for unselecting */
const unselectOption = _computed(() => ({
  [__props.labelProp]: __props.unselectLabel,
  [__props.valueProp]: '__unselect__',
  unselect: true,
}))

/** All items to display in dropdown/action sheet */
const displayedItems = _computed(() => [
  ...(!filteringBy.value.length ? __props.emptyQueryOptions : []),
  showUnselectOption.value && unselectOption.value,
  showCreateOption.value && createOption.value,
  ...filteredItems.value,
].filter(Boolean))

/** Whether to show empty state */
const showEmptyState = _computed(() =>
  !fetchingRemoteItems.value
  && displayedItems.value.length === 0
  && (!__props.fetchOptions || filteringBy.value.length > 0),
)

/** Whether the dropdown has any content to show */
const hasDropdownContent = _computed(() =>
  fetchingRemoteItems.value || displayedItems.value.length > 0 || showEmptyState.value,
)

// =============================================================================
// Sync modelValue → selectedItems
// =============================================================================

function getSelectedItemsFromValue (rawValue: any) {
  if (isNil(rawValue)) return []

  const val = coerceValue(rawValue)
  const values = __props.multi ? [...val] : [val]

  return values.map((v) => {
    const extracted = extractValue(v)
    return findItemByValue(allItems.value, extracted)
      || (__props.fetchOptions && findItemByValue(selectedItems.value, extracted))
      || (__props.allowCreate && createNewItem(v))
  }).filter(Boolean)
}

watchEffect(() => {
  selectedItems.value = getSelectedItemsFromValue(__props.modelValue)
})

function emitValue () {
  const toValue = __props.wholeItem ? identity : (item: any) => item[__props.valueProp]
  const values = selectedItems.value.map(toValue).map(coerceValue)
  emit('update:modelValue', __props.multi ? values : values[0])
}

// =============================================================================
// Desktop: Combobox handlers
// =============================================================================

function onComboboxChange (val: any) {
  if (!val) return

  // Handle array for multi-select
  const items = Array.isArray(val) ? val : [val]
  const lastItem = items[items.length - 1]

  // For single-select, remove previously created item when changing selection
  if (!__props.multi && selectedItems.value.length > 0) {
    removeCreatedItem(selectedItems.value[0])
  }

  // Handle special items
  if (lastItem?.unselect) {
    selectedItems.value = []
  }
  else if (lastItem?.creatable) {
    const newItem = createNewItem(filteringBy.value)
    if (__props.multi) {
      selectedItems.value = [...items.slice(0, -1), newItem]
    }
    else {
      selectedItems.value = [newItem]
    }
  }
  else if (__props.multi) {
    selectedItems.value = items
  }
  else {
    selectedItems.value = items.slice(-1)
  }

  filteringBy.value = ''
  // Clear the input in multi mode so user can continue typing
  if (__props.multi && comboboxInputRef.value) {
    const input = comboboxInputRef.value.$el ?? comboboxInputRef.value
    if (input) input.value = ''
  }
  emitValue()
}

function displayValue (item: any) {
  if (!item) return ''
  if (Array.isArray(item)) return ''
  const label = item[__props.selectedProp || __props.labelProp] || ''
  // Only apply suffix to user-created items, not preset options
  return label ? `${label}${isCreatedItem(item) ? __props.suffix : ''}` : ''
}

function onInputChange (event: Event) {
  filteringBy.value = (event.target as HTMLInputElement).value
}

let justFocused = false

function onInputFocus () {
  justFocused = true
  // Open dropdown on focus (if not already open)
  nextTick(() => {
    const wrapper = comboboxButtonWrapperRef.value
    const btn = wrapper?.querySelector('button') as HTMLButtonElement | null
    if (btn && btn.getAttribute('aria-expanded') !== 'true') {
      btn.click()
    }
    // Select all text so typing replaces it
    if (__props.allowCreate || __props.searchable) {
      const input = comboboxInputRef.value?.$el ?? comboboxInputRef.value
      if (input) input.select()
    }
    setTimeout(() => { justFocused = false }, 100)
  })
  emit('focus')
}

function onInputClick () {
  // Skip if just focused (focus handler already opened it)
  if (justFocused) return
  // Toggle dropdown on click
  const wrapper = comboboxButtonWrapperRef.value
  const btn = wrapper?.querySelector('button') as HTMLButtonElement | null
  btn?.click()
}

function onInputKeydown (event: KeyboardEvent) {
  // Handle Enter to create new item in single-select mode
  if (event.key === 'Enter' && __props.allowCreate && !__props.multi && showCreateOption.value) {
    event.preventDefault()
    // Remove previously created item
    if (selectedItems.value.length > 0) {
      removeCreatedItem(selectedItems.value[0])
    }
    const newItem = createNewItem(filteringBy.value)
    selectedItems.value = [newItem]
    filteringBy.value = ''
    emitValue()
  }
}

function onMobileSearchKeydown (event: KeyboardEvent) {
  if (event.key !== 'Enter') return
  event.preventDefault()

  // For single-select with custom value, create and confirm
  if (!__props.multi && __props.allowCreate && showCreateOption.value) {
    if (pendingItems.value.length > 0) {
      removeCreatedItem(pendingItems.value[0])
    }
    const newItem = createNewItem(filteringBy.value)
    pendingItems.value = [newItem]
  }

  // Confirm selection
  if (!__props.multi || pendingItems.value.length > 0) {
    selectedItems.value = [...pendingItems.value]
    emitValue()
    emit('confirm', __props.multi ? selectedItems.value : selectedItems.value[0])
    closeMobile()
  }
}

// =============================================================================
// Mobile: Action Sheet handlers
// =============================================================================

function openMobile () {
  if (__props.disabled) return
  pendingItems.value = [...selectedItems.value]
  filteringBy.value = ''
  mobileOpen.value = true
  emit('focus')
  nextTick(() => mobileSearchInputRef.value?.focus())
}

function closeMobile () {
  mobileOpen.value = false
  filteringBy.value = ''
  emit('blur')
}

function onCancel () {
  emit('cancel')
  closeMobile()
}

function onConfirm () {
  selectedItems.value = [...pendingItems.value]
  emitValue()
  emit('confirm', __props.multi ? selectedItems.value : selectedItems.value[0])
  closeMobile()
}

function selectMobileItem (item: any) {
  // For single-select, remove previously created item when changing selection
  if (!__props.multi && pendingItems.value.length > 0) {
    removeCreatedItem(pendingItems.value[0])
  }

  if (item.unselect) {
    pendingItems.value = []
  }
  else if (item.creatable) {
    const newItem = createNewItem(filteringBy.value)
    if (__props.multi) {
      if (!findItemByValue(pendingItems.value, newItem[__props.valueProp])) {
        pendingItems.value = [...pendingItems.value, newItem]
      }
    }
    else {
      pendingItems.value = [newItem]
    }
    filteringBy.value = ''
  }
  else if (__props.multi) {
    if (!findItemByValue(pendingItems.value, item[__props.valueProp])) {
      pendingItems.value = [...pendingItems.value, item]
    }
  }
  else {
    pendingItems.value = [item]
  }

  // Auto-confirm for single-select when autoConfirm is enabled
  if (__props.autoConfirm && !__props.multi) {
    nextTick(() => onConfirm())
  }
}

function unselectMobileItem (item: any) {
  pendingItems.value = pendingItems.value.filter(i => i[__props.valueProp] !== item[__props.valueProp])
  removeCreatedItem(item)
}

function isMobileItemSelected (item: any): boolean {
  if (item.creatable || item.unselect) return false
  return !!findItemByValue(pendingItems.value, item[__props.valueProp])
}

// Desktop: remove badge
function unselectDesktopItem (item: any) {
  const removed = removeBy(selectedItems.value, i => i[__props.valueProp] === item[__props.valueProp])
  if (removed) {
    removeCreatedItem(item)
    emitValue()
  }
}

// =============================================================================
// Remote Fetch
// =============================================================================

const debouncedFetch = typeaheadDebounce(async (term: string) => {
  if (!term.length) {
    remoteItems.value = undefined
    fetchingRemoteItems.value = false
    return
  }

  fetchingRemoteItems.value = true
  try {
    remoteItems.value = await __props.fetchOptions?.({ query: { term }, visit: false })
  }
  catch (error) {
    console.error('Error fetching remote items:', error)
  }
  fetchingRemoteItems.value = false
})

if (__props.fetchOptions) {
  watch((filteringBy), term => debouncedFetch(term))
}

// =============================================================================
// Utilities
// =============================================================================

/** Highlight matching text in label */
function highlightLabel (label: string) {
  if (!filteringBy.value) return label
  const tokens = sortedTokensFor(filteringBy.value)
  let result = label
  for (const token of tokens) {
    result = result.replace(buildRegExp(token), '<mark class="bg-yellow-100">$1</mark>')
  }
  return result
}

const messages = {
  create: 'Type to create a new item…',
  empty: 'No items available',
  searchPlaceholder: 'Search...',
  searchOrCreate: 'Search or add new...',
}

// =============================================================================
// Mobile Action Sheet Transitions
// =============================================================================

const sheetTransition = {
  enter: 'transition ease-in-out duration-300 transform',
  enterFrom: 'translate-y-full',
  enterTo: 'translate-y-0',
  leave: 'transition ease-in-out duration-300 transform',
  leaveFrom: 'translate-y-0',
  leaveTo: 'translate-y-full',
}

preventWindowScroll({ when: () => mobileOpen.value && isActionSheet.value })

// =============================================================================
// Expose
// =============================================================================

defineExpose({
  expand: openMobile,
  collapse: closeMobile,
})
</script>

<template>
  <div class="g-sheet-select-wrapper">
    <!-- ===== Mobile: Trigger + Action Sheet ===== -->
    <template v-if="isActionSheet">
      <!-- Trigger Button -->
      <button
        type="button"
        class="g-sheet-select-trigger"
        :class="{
          disabled,
          pill,
          'has-errors': hasErrors,
          'has-multi-selection': multi && selectedItems.length > 0,
          [size]: true,
        }"
        :disabled="disabled"
        @click="openMobile"
      >
        <div v-if="multi && selectedItems.length > 0" class="g-sheet-select-badges">
          <GBadge
            v-for="item in selectedItems"
            :key="`trigger-${item[valueProp]}`"
            type="chip"
            :closable="!disabled"
            :label="labelWithSuffix(item)"
            size="md"
            @close="unselectMobileItem(item)"
            @click.stop
          >
            <template v-if="$slots.badgePre" #pre>
              <slot name="badgePre" :item="item"/>
            </template>
          </GBadge>
        </div>
        <span v-else class="g-sheet-select-value" :class="{ placeholder: !selectedItem }">
          <slot name="inner" :selectedItem="selectedItem" :filteringBy="filteringBy"/>
          {{ selectedItem ? labelWithSuffix(selectedItem) : placeholderProp }}
        </span>
        <GIcon
          class="g-sheet-select-chevron"
          :class="{ 'opacity-20': disabled }"
          :icon="faChevronDown"
          size="xs"
        />
      </button>

      <!-- Action Sheet -->
      <TransitionRoot as="template" :show="mobileOpen">
        <Dialog as="div" class="fixed inset-0 z-dialog flex items-end" @close="onCancel">
          <!-- Backdrop -->
          <TransitionChild
            as="template"
            enter="transition-opacity ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-in-out duration-700"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div class="fixed inset-0 bg-black/40"/>
          </TransitionChild>

          <!-- Panel -->
          <TransitionChild as="template" v-bind="sheetTransition">
            <DialogPanel
              class="relative bg-white w-full rounded-t-v2lg flex flex-col overflow-hidden"
              style="max-height: calc(100svh - 1rem);"
            >
              <!-- Header -->
              <div class="g-sheet-select-header">
                <button type="button" class="g-sheet-select-header-btn cancel" @click="onCancel">
                  {{ cancelLabel }}
                </button>
                <span v-if="title" class="g-sheet-select-header-title">{{ title }}</span>
                <button
                  v-if="multi || !autoConfirm"
                  type="button"
                  class="g-sheet-select-header-btn confirm"
                  @click="onConfirm"
                >
                  {{ confirmLabel }}
                </button>
                <span v-else class="g-sheet-select-header-btn"/>
              </div>

              <!-- Search -->
              <div v-if="searchable || allowCreate">
                <input
                  ref="mobileSearchInputRef"
                  v-model="filteringBy"
                  v-mask="inputMask"
                  type="text"
                  class="g-sheet-select-search"
                  :placeholder="searchPlaceholderProp ||
                    (allowCreate ? messages.searchOrCreate : messages.searchPlaceholder)"
                  @keydown="onMobileSearchKeydown"
                >
              </div>

              <!-- Pending Badges (multi) -->
              <div v-if="multi && pendingItems.length > 0" class="px-4 pb-3">
                <div class="g-sheet-select-badges">
                  <GBadge
                    v-for="item in pendingItems"
                    :key="`pending-${item[valueProp]}`"
                    type="chip"
                    closable
                    :label="labelWithSuffix(item)"
                    size="md"
                    @close="unselectMobileItem(item)"
                  />
                </div>
              </div>

              <!-- Options -->
              <div class="flex-1 overflow-y-auto px-4 pb-4">
                <div class="flex flex-col">
                  <button
                    v-for="item in displayedItems"
                    :key="item[valueProp]"
                    type="button"
                    class="g-sheet-select-item"
                    :class="{
                      selected: isMobileItemSelected(item),
                      unselect: item.unselect,
                      creatable: item.creatable,
                    }"
                    @click="selectMobileItem(item)"
                  >
                    <div class="g-sheet-select-item-content">
                      <span v-if="item.creatable" class="g-sheet-select-item-label">
                        {{ createLabel }} <strong>{{ item.label ?? item[labelProp] }}</strong>{{ suffix }}…
                      </span>
                      <span v-else-if="item.unselect" class="g-sheet-select-item-label">{{ item[labelProp] }}</span>
                      <span v-else class="g-sheet-select-item-label">{{ item[labelProp] }}</span>
                      <span v-if="item[descriptionProp]" class="g-sheet-select-item-desc">
                        {{ item[descriptionProp] }}
                      </span>
                    </div>
                    <GIcon v-if="isMobileItemSelected(item)" class="text-v2-active" :icon="faCheck"/>
                  </button>

                  <div v-if="showEmptyState" class="g-sheet-select-empty">
                    {{ allowCreate ? messages.create : messages.empty }}
                  </div>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </TransitionRoot>
    </template>

    <!-- ===== Desktop: Headless UI Combobox ===== -->
    <Combobox
      v-else
      :modelValue="multi ? selectedItems : selectedItem"
      :multiple="multi"
      :disabled="disabled"
      as="div"
      class="g-sheet-select-desktop"
      :class="{ disabled, 'has-errors': hasErrors }"
      @update:modelValue="onComboboxChange"
    >
      <Float
        placement="bottom-start"
        :offset="8"
        flip
        portal
        adaptiveWidth
        enter="transition duration-200 ease-out"
        enterFrom="opacity-0 scale-95 origin-top"
        enterTo="opacity-100 scale-100 origin-top"
        leave="transition duration-150 ease-in"
        leaveFrom="opacity-100 scale-100 origin-top"
        leaveTo="opacity-0 scale-95 origin-top"
      >
        <div
          class="g-sheet-select-input-wrapper"
          :class="{ multi, pill, [size]: true, 'has-errors': hasErrors }"
          :data-name="name"
        >
          <!-- Multi-select badges -->
          <template v-if="multi && selectedItems.length > 0">
            <GBadge
              v-for="item in selectedItems"
              :key="`badge-${item[valueProp]}`"
              type="chip"
              :closable="!disabled"
              :label="labelWithSuffix(item)"
              size="md"
              @close="unselectDesktopItem(item)"
              @click.stop
            >
              <template v-if="$slots.badgePre" #pre>
                <slot name="badgePre" :item="item"/>
              </template>
            </GBadge>
          </template>

          <!-- Single-select inner slot (e.g., for icon before text) -->
          <slot v-if="!multi" name="inner" :selectedItem="selectedItem" :filteringBy="filteringBy"/>

          <ComboboxInput
            ref="comboboxInputRef"
            v-mask="filteringBy ? inputMask : undefined"
            class="g-sheet-select-input"
            :class="{
              multi: multi && selectedItems.length > 0,
              'cursor-pointer': !allowCreate && !autocomplete && !searchable,
            }"
            :placeholder="multi && selectedItems.length > 0 ? '' : placeholderProp"
            :displayValue="displayValue"
            :readonly="!allowCreate && !autocomplete && !searchable"
            @change="onInputChange"
            @focus="onInputFocus"
            @click="onInputClick"
            @blur="emit('blur')"
            @keydown="onInputKeydown"
          />

          <span ref="comboboxButtonWrapperRef" class="contents">
            <ComboboxButton class="g-sheet-select-btn">
              <GIcon
                class="select-input-arrow"
                :class="{ 'opacity-20': disabled }"
                :icon="faChevronDown"
                size="xs"
              />
            </ComboboxButton>
          </span>
        </div>

        <ComboboxOptions
          v-show="hasDropdownContent"
          class="g-sheet-select-options"
          :class="[size, { pill }]"
          :data-name="name"
        >
          <!-- Loading state -->
          <div v-if="fetchingRemoteItems" class="g-sheet-select-option loading">
            <LoadingIndicator/>
          </div>

          <!-- Options -->
          <template v-else>
            <ComboboxOption
              v-for="item in displayedItems"
              v-slot="{ active, selected }"
              :key="item[valueProp]"
              :value="item"
              as="template"
            >
              <li
                class="g-sheet-select-option"
                :class="{
                  active,
                  selected,
                  'text-v2-tertiary italic': item.unselect,
                  'text-v2-active': item.creatable,
                }"
              >
                <slot name="itemPre" :item="item" :filteringBy="filteringBy"/>
                <div class="flex-1">
                  <div v-if="item.creatable">
                    {{ createLabel }} <strong>{{ item[labelProp] }}</strong>{{ suffix }}…
                  </div>
                  <div v-else>
                    <span v-html="highlightLabel(item[labelProp])"/>
                  </div>
                  <div v-if="item[descriptionProp]" class="text-v2-tertiary">
                    {{ item[descriptionProp] }}
                  </div>
                </div>
                <slot name="itemPost" :item="item" :filteringBy="filteringBy"/>
                <GIcon v-if="selected" class="text-v2-active shrink-0" :icon="faCheck"/>
              </li>
            </ComboboxOption>
          </template>

          <!-- Empty state -->
          <div v-if="showEmptyState" class="g-sheet-select-option empty">
            {{ allowCreate ? messages.create : messages.empty }}
          </div>
        </ComboboxOptions>
      </Float>
    </Combobox>
  </div>
</template>

<style lang="scss">
// Wrapper
.g-sheet-select-wrapper {
  @apply contents;
}

// =============================================================================
// Desktop: Combobox Styles
// =============================================================================

.g-sheet-select-desktop {
  @apply relative w-full;

  &.disabled {
    @apply opacity-50 pointer-events-none;
  }
}

.g-sheet-select-input-wrapper {
  @apply relative flex flex-wrap items-center gap-1;
  @apply bg-white font-medium text-navy;

  border: 1px solid var(--grey-primary);
  border-radius: theme("borderRadius.v2md");
  padding: 0.875rem 2.5rem 0.875rem 1.25rem;
  transition: border-color 0.15s;

  &:hover {
    border-color: var(--theme-color-primary-base);
  }

  &:focus-within {
    border-color: var(--theme-color-primary-base);
  }

  &.small {
    @apply py-2 px-3 pr-8 text-sm rounded-v2sm;
  }

  &.pill {
    border-radius: 9999px;
    padding-left: 1.25rem;
    padding-right: 2.5rem;
  }

  &.has-errors {
    border-color: theme("colors.red.base");
  }

  &.multi {
    @apply py-2 px-3 pr-8;

    min-height: 56px;

    &.pill {
      border-radius: theme("borderRadius.v2lg");
      padding: 0.5rem 2.5rem 0.5rem 0.75rem;
    }
  }
}

.g-sheet-select-input {
  @apply flex-1 min-w-[80px] bg-transparent outline-none;
  @apply placeholder:text-v2-tertiary placeholder:opacity-100;

  &.multi {
    @apply min-w-[40px];
  }
}

.g-sheet-select-btn {
  @apply absolute inset-y-0 right-0 flex items-center pr-5;

  .select-input-arrow {
    transition: transform 0.1s ease-out;
  }

  &[aria-expanded="true"] .select-input-arrow {
    transform: translateY(2px) rotate(180deg);
  }
}

.g-sheet-select-options {
  @apply py-1 bg-white drop-shadow-dropdown;
  @apply overflow-auto;

  border: 1px solid var(--grey-primary);
  border-radius: theme("borderRadius.md");
  max-height: 200px;
  min-width: 200px;

  &.pill {
    @apply py-3;

    border-radius: theme("borderRadius.v2lg");
  }

  &.small .g-sheet-select-option {
    @apply p-3 text-xs;
  }
}

.g-sheet-select-option {
  @apply flex items-center gap-2 px-4 py-3;
  @apply cursor-pointer text-navy font-medium;
  @apply select-none;

  &.active,
  &.focused {
    @apply text-navy bg-surface-grey;
  }

  &.selected {
    @apply text-v2-active;
  }

  &.loading,
  &.empty {
    @apply text-v2-tertiary italic cursor-default;
  }
}

// =============================================================================
// Mobile Trigger
// =============================================================================

.g-sheet-select-trigger {
  @apply w-full flex items-center justify-between gap-2;
  @apply bg-white font-medium text-left text-navy cursor-pointer;

  border: 1px solid var(--grey-primary);
  border-radius: theme("borderRadius.v2md");
  padding: 0.875rem 1.25rem;
  transition: border-color 0.15s;

  &.pill {
    span {
      @apply truncate;
    }

    border-radius: 9999px;
    padding-left: 1.25rem;
    padding-right: 1.25rem;
  }

  &.small {
    @apply py-2 px-3 text-sm rounded-v2sm;
  }

  &:hover:not(.disabled) {
    border-color: var(--theme-color-primary-base);
  }

  &:focus {
    border-color: var(--theme-color-primary-base);
    box-shadow: 0 0 0 1px var(--theme-color-primary-base);
    outline: none;
    z-index: 10;
  }

  &.disabled {
    @apply opacity-50 cursor-not-allowed;
  }

  &.has-errors {
    border-color: theme("colors.red.base");
    color: theme("colors.red.base");
  }

  &.has-multi-selection {
    @apply flex-wrap py-2 px-3;

    min-height: 56px;
  }
}

.g-sheet-select-value {
  @apply flex-1 flex items-center gap-2;

  &.placeholder {
    @apply text-v2-tertiary;
  }
}

.g-sheet-select-chevron {
  @apply shrink-0;

  transition: transform 0.1s ease-out;
}

// =============================================================================
// Shared: Badges
// =============================================================================

.g-sheet-select-badges {
  @apply flex flex-wrap gap-1.5 flex-1;
}

// =============================================================================
// Mobile Action Sheet
// =============================================================================

.g-sheet-select-header {
  @apply flex items-center justify-between px-4 py-4;
}

.g-sheet-select-header-btn {
  @apply text-base font-medium;

  &.cancel {
    @apply text-v2-secondary;
  }

  &.confirm {
    @apply text-v2-active;
  }
}

.g-sheet-select-header-title {
  @apply text-base font-semibold text-navy;
}

.g-sheet-select-search {
  @apply w-full px-4 py-3 text-base font-medium;
  @apply border border-grey-primary rounded-v2md;
  @apply focus:outline-none focus:border-theme-color-primary-base focus:ring-1 focus:ring-theme-color-primary-base;

  &::placeholder {
    @apply text-v2-tertiary;
  }
}

.g-sheet-select-item {
  @apply flex items-center justify-between w-full text-left;
  @apply py-3 text-navy font-medium;

  &.selected {
    @apply text-v2-active;
  }

  &.unselect {
    @apply text-v2-secondary italic;
  }

  &.creatable {
    @apply text-v2-active;
  }
}

.g-sheet-select-item-content {
  @apply flex flex-col gap-0.5 flex-1;
}

.g-sheet-select-item-label {
  @apply text-base;
}

.g-sheet-select-item-desc {
  @apply text-v2-tertiary font-normal;
}

.g-sheet-select-empty {
  @apply py-3 text-v2-tertiary text-sm italic;
}
</style>
